import { Label } from '@/components/ui';
import { cn } from '@/helpers/ui-utils';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cva, VariantProps } from 'class-variance-authority';
import * as React from 'react';
import HelperText from '../helperText';
import Progress from './progress';

const progressBarVariants = cva(['bg-card', 'group'], {
  variants: {
    variant: {
      default: ['[&_.progress-indicator]:bg-primary'],
      secondary: ['[&_.progress-indicator]:bg-secondary'],
      complete: ['[&_.progress-indicator]:bg-[#22C55E]'],
      error: ['[&_.progress-indicator]:bg-[#D82246]'],
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface ProgressBarProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof progressBarVariants> {
  /**
   * Unique identifier for the progress bar element. Used for accessibility and form association.
   */
  id: string;

  /**
   * The label text displayed above the progress bar. Provides context about what is being tracked.
   */
  label: string;

  /**
   * The current progress value. Should be between 0 and the max value.
   */
  value: number;

  /**
   * The maximum value for the progress bar. Defines the upper limit of the progress scale.
   * @default 100
   */
  max?: number;

  /**
   * Optional descriptive text displayed below the progress bar. Useful for providing additional context or status information.
   */
  helperText?: string;

  /**
   * Additional classes for the label. Merged with (and can override) the label's default styling.
   */
  labelClassName?: string;

  /**
   * Gap between the label row and the progress track, as a Tailwind gap-* class.
   * @default 'gap-3'
   */
  gapClassName?: string;

  /**
   * Whether to display the percentage value next to the label. Shows the current progress as a percentage.
   * @default false
   */
  showPercentage?: boolean;
}

/**
 * Display task progress with customizable styling, percentage indicators, and contextual variants for different states.
 *
 * @param props - ProgressBar properties. See {@link ProgressBarProps} for detailed prop documentation including inherited CVA variants.
 * @param ref - Forwarded ref to the progress element
 * @returns Rendered progress bar component
 *
 * @example
 * ```tsx
 * // Basic progress bar
 * <ProgressBar id="upload" label="Upload Progress" value={75} />
 *
 * // Progress bar with percentage display
 * <ProgressBar
 *   id="download"
 *   label="Download"
 *   value={45}
 *   showPercentage
 *   helperText="Downloading files..."
 * />
 *
 * // Complete state progress bar
 * <ProgressBar
 *   id="complete"
 *   label="Task Complete"
 *   value={100}
 *   variant="complete"
 *   showPercentage
 * />
 *
 * // Error state progress bar
 * <ProgressBar
 *   id="error"
 *   label="Upload Failed"
 *   value={30}
 *   variant="error"
 *   helperText="An error occurred during upload"
 * />
 * ```
 */
const ProgressBar = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressBarProps
>(
  (
    {
      className,
      variant = 'default',
      label,
      labelClassName,
      gapClassName = 'gap-3',
      helperText,
      showPercentage,
      max = 100,
      value = 0,
      id,
      ...props
    },
    ref,
  ) => {
    const styles = progressBarVariants({ variant, className });
    // The `role="progressbar"` element Radix renders has no accessible name of
    // its own -- associate it with the visible label so screen readers (and
    // axe's aria-progressbar-name check) can announce it.
    const labelId = `${id}-label`;

    return (
      <div className={cn(['flex', 'flex-col', gapClassName])}>
        <div
          className={cn([
            'flex',
            'flex-row',
            'justify-between',
            'items-center',
          ])}
        >
          <Label id={labelId} className={cn('text-md', labelClassName)}>
            {label}
          </Label>
          {showPercentage && (
            <span className="font-nunito-sans text-md text-foreground/95">
              {value >= max ? max : value}%
            </span>
          )}
        </div>
        <Progress
          id={id}
          ref={ref}
          max={max}
          value={value}
          aria-labelledby={labelId}
          className={cn(styles)}
          {...props}
        />

        {helperText && <HelperText id={id}>{helperText}</HelperText>}
      </div>
    );
  },
);

export default ProgressBar;
